// Organization types
export enum OrganizationType {
  REGULAR = 'regular',
}

export interface Organization {
  id: string;
  parentOrganizationId?: string;
  name: string;
  code?: string;
  type: OrganizationType;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  subscriptionPlan: string;
  isActive: boolean;
  createdAt: string;
  parentOrganization?: Organization;
  childOrganizations?: Organization[];
  // SMS Configuration
  smsEnabled?: boolean;
  smsUsercode?: string;
  smsPassword?: string;
  smsMsgheader?: string;
  // Email Configuration
  emailEnabled?: boolean;
  emailProvider?: string;
  emailApiKey?: string;
  emailFromEmail?: string;
  emailFromName?: string;
  emailAwsRegion?: string;
  emailAwsAccessKeyId?: string;
  emailAwsSecretAccessKey?: string;
  // Meet & Greet Fee
  meetGreetFee?: number;
  // Guest Email Settings
  guestEmailSenderName?: string;
  guestEmailMeetingPoint?: string;
}

// User types
export enum UserRole {
  PLATFORM_ADMIN = 'platform_admin',
  ORG_ADMIN = 'org_admin',
  MANAGER = 'manager',
  STAFF = 'staff',
  PROVIDER_USER = 'provider_user',
  PROVIDER_MANAGER = 'provider_manager',
  POOL_MEMBER = 'pool_member',
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  organizationId?: string;
  providerId?: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  assignedProviders?: Provider[];
  transferPermissions?: string[] | null;
  provider?: Provider;
}

// Transfer types
export enum TransferStatus {
  AVAILABLE = 'available',
  CLAIMED = 'claimed',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum TripType {
  ARRIVAL = 'arrival',
  DEPARTURE = 'departure',
}

export enum Currency {
  TRY = 'TRY',
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
}

export interface Transfer {
  id: string;
  transferCode?: string;
  organizationId: string;
  assignedOrganizationId?: string;
  assignedProviderId?: string;
  assignedDriverId?: string;
  whatsappDriverName?: string;
  whatsappDriverPhone?: string;
  whatsappDriverPlate?: string;
  providerDriverName?: string;
  providerDriverPhone?: string;
  providerPlate?: string;
  providerDriverId?: string | null;
  providerVehicleTypeId?: string | null;
  providerExternalVehicleType?: string | null;
  transferDate: string;
  transferTime: string;
  returnTime?: string;
  tripType?: TripType;
  currency?: Currency;
  customerId?: string | null;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  tcPassport?: string;
  passportFile?: string;
  flightCode?: string;
  country?: string;
  pickupLocation: string;
  dropoffLocation: string;
  vehicleType: string;
  passengerCount: number;
  specialNotes?: string;
  customerPrice: number;
  providerPrice?: number;
  providerPaid?: boolean;
  extraExpenses?: number;
  meetGreetFee?: number;
  netProfit?: number;
  matchedTransferId?: string | null;
  poolGroupId?: string | null;
  status: TransferStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  assignedDriver?: Driver;
  assignedProvider?: Provider;
  assignedOrganization?: Organization;
  claimedByPoolMember?: { id: string; userId: string; fullName: string; phone?: string; poolMemberPaid: boolean } | null;
  guests?: Guest[];
  matchedTransfer?: Transfer;
  providerDriver?: Driver;
  providerVehicleType?: VehicleType;
}

// Guest types
export interface Guest {
  id?: string;
  name: string;
  phone?: string;
  email?: string;
  tcPassport?: string;
  passportFile?: string;
}

// Payment Mode enum (for provider finance)
export enum PaymentMode {
  PER_TRANSFER = 'per_transfer',
  LUMP_SUM = 'lump_sum',
}

// Pricing types
export enum PricingDirection {
  ARRIVAL = 'arrival',
  DEPARTURE = 'departure',
  BOTH = 'both',
}

export enum PricingType {
  PROVIDER = 'provider',
  CUSTOMER = 'customer',
}

export interface Customer {
  id: string;
  organizationId: string;
  name: string;
  phone?: string;
  email?: string;
  tcPassport?: string;
  country?: string;
  notes?: string;
  isActive: boolean;
  paymentMode: PaymentMode;
  balance: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface CustomerLocation {
  id: string;
  customerId?: string | null;
  locationName: string;
  address?: string;
  isGlobal: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerProviderMapping {
  id: string;
  organizationId: string;
  customerId: string;
  providerId: string;
  isActive: boolean;
  createdAt: string;
  customer?: Customer;
  provider?: Provider;
}

export interface Pricing {
  id: string;
  organizationId: string;
  pricingType: PricingType;
  providerId?: string | null;
  customerId?: string | null;
  pickupLocation: string;
  dropoffLocation: string;
  direction: PricingDirection;
  vehicleTypeId?: string | null;
  price: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  provider?: Provider;
  customer?: Customer;
  vehicleType?: VehicleType;
}

// Driver types
export interface Driver {
  id: string;
  organizationId: string;
  providerId?: string | null;
  fullName: string;
  phone: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  provider?: Provider;
}

// Provider types
export enum ProviderType {
  COMPANY = 'company',
  WHATSAPP = 'whatsapp',
}

export enum PricingModel {
  MANUAL = 'manual',
  FIXED = 'fixed',
}

export interface Provider {
  id: string;
  organizationId: string;
  name: string;
  providerType?: ProviderType;
  pricingModel?: PricingModel;
  paymentMode: PaymentMode;
  balance: number;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Vehicle Type types
export interface VehicleType {
  id: string;
  organizationId: string;
  providerId?: string | null;
  name: string;
  licensePlate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  provider?: Provider;
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  organizationName: string;
  organizationPhone: string;
  fullName: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// Dashboard types
export interface DashboardStats {
  todayTransfers: number;
  totalTransfers: number;
  totalRevenue: number;
  totalProfit: number;
}

// Filter types
export interface TransferFilters {
  status?: TransferStatus;
  excludeStatus?: TransferStatus;
  activeOnly?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  assignedDriverId?: string;
  assignedProviderId?: string;
  tripType?: TripType;
  vehicleType?: string;
  page?: string;
  limit?: string;
}

// Analytics types
export interface WeeklyTrend {
  date: string;
  transfers: number;
  revenue: number;
}

export interface StatusDistribution {
  status: string;
  count: number;
}

export interface MonthlyComparison {
  month: string;
  transfers: number;
  revenue: number;
  cost: number;
  profit: number;
}

export interface VehicleTypeDistribution {
  vehicleType: string;
  count: number;
}

// Form Submission types
export enum FormSubmissionStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum FormDirection {
  ARRIVAL = 'arrival',
  DEPARTURE = 'departure',
}

export interface FormSubmission {
  id: string;
  organizationId: string;
  formSource: string;
  direction: FormDirection;
  transferDate: string;
  transferTime: string;
  airport: string;
  location: string;
  flightCode: string | null;
  guestCount: number;
  guestNames: string;
  description: string | null;
  filledBy: string;
  externalRefNo?: string | null;
  pickupLocation: string;
  dropoffLocation: string;
  rawData: Record<string, any>;
  extraFields: Record<string, any> | null;
  status: FormSubmissionStatus;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  assignedProviderId?: string | null;
  assignedProvider?: { id: string; name: string; providerType: ProviderType } | null;
  customerPrice?: number | null;
  providerPrice?: number | null;
  createdTransferId: string | null;
  createdAt: string;
  updatedAt: string;
  reviewedByUser?: User | null;
}

export interface FormSubmissionStats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export interface ApproveFormSubmissionData {
  transferDate?: string;
  transferTime?: string;
  tripType?: TripType;
  pickupLocation?: string;
  dropoffLocation?: string;
  flightCode?: string;
  passengerCount?: number;
  specialNotes?: string;
  customerName?: string;
  assignedProviderId?: string;
  assignedDriverId?: string;
  vehicleType?: string;
  providerDriverName?: string;
  providerDriverPhone?: string;
  providerPlate?: string;
  providerPrice?: number;
  meetGreetFee?: number;
}
